import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, List, Typography } from '@mui/material';
import type { RootState } from '../../redux/rootReducer';
import type { AppDispatch } from '../../redux/store';
import {
  deriveChannelState,
  mapAdvisoryErrorCode,
  startAdvisoryListListener,
  stopAdvisoryListListener,
} from '../../redux/slices/advisory';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import { NewEngagementRow } from './components/NewEngagementRow';
import { EngagementListSkeleton } from './components/EngagementListSkeleton';
import { AdvisoryCalloutState } from './components/AdvisoryCalloutState';
import { EngagementRow } from './components/EngagementRow';
import { pageSurface, contentColumn, heading } from '../../styles/visualLanguage';

/**
 * AdvisoryListPage — the `/advisory` route.
 *
 * Auth guard: duplicates `ChatPage.tsx`'s inline redirect pattern in
 * shape — read, never modified. No shared `<RequireAuth>` is extracted
 * here; that is an explicitly deferred item.
 *
 * Listener lifecycle: unconditional start on mount, unconditional stop on
 * unmount — `advisoryListeners.ts`'s remove-then-set registration is
 * already idempotent, so no guard is added here.
 */
export function AdvisoryListPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    loading: userLoading,
  } = useSelector((state: RootState) => state.user);
  const engagementsChannel = useSelector((state: RootState) => state.advisory.engagementsChannel);
  const engagements = useSelector((state: RootState) => state.advisory.engagements);
  const { t } = useUntypedTranslation();

  // Redirecting during auth bootstrap would bounce a signed-in user off
  // their own page, so this waits for `userLoading` to finish.
  useEffect(() => {
    if (!userLoading && !isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, userLoading, navigate]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    const userId = user.id;
    dispatch(startAdvisoryListListener(userId));
    return () => {
      dispatch(stopAdvisoryListListener(userId));
    };
  }, [dispatch, user?.id]);

  const channelState = deriveChannelState(engagementsChannel);

  const handleRetry = () => {
    if (user?.id) {
      dispatch(startAdvisoryListListener(user.id));
    }
  };

  let content: React.ReactNode = null;

  switch (channelState) {
    case 'loading':
      content = <EngagementListSkeleton />;
      break;
    case 'empty':
      content = (
        <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
          <Typography variant="h6" sx={[heading]}>
            {t('advisory:list.empty.heading')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('advisory:list.empty.body')}
          </Typography>
        </Box>
      );
      break;
    case 'denied':
    case 'failed': {
      // Copy, tone and retry availability all come from
      // `mapAdvisoryErrorCode`, never from local logic.
      const mapping = mapAdvisoryErrorCode(engagementsChannel.errorCode);
      const headingKey =
        channelState === 'denied'
          ? 'advisory:callout.denied.heading'
          : 'advisory:callout.failed.heading';
      content = (
        <AdvisoryCalloutState
          tone={mapping.tone}
          headingKey={headingKey}
          bodyKey={mapping.i18nKey}
          onRetry={mapping.retryable ? handleRetry : undefined}
        />
      );
      break;
    }
    case 'ready':
      // Rows render in store order — the Firestore query already applies
      // orderBy('updatedAt', 'desc'), so no client-side re-sort is added
      // here. `failed` engagements are not filtered out.
      content = engagements.map(engagement => (
        <EngagementRow key={engagement.id} engagement={engagement} />
      ));
      break;
  }

  return (
    <Box sx={[pageSurface, { p: { xs: 2, sm: 3 } }]}>
      <Typography
        variant="h6"
        sx={[
          heading,
          contentColumn,
          theme => ({ marginBottom: theme.visualPrimitives.headingGap }),
        ]}
      >
        {t('advisory:list.heading')}
      </Typography>
      <List sx={[contentColumn, { p: 0 }]}>
        <NewEngagementRow />
        {content}
      </List>
    </Box>
  );
}

export default AdvisoryListPage;
